import { useCallback, useMemo, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { COMMON_TRANSLATE_KEYS } from '../../../../constants/translate-keys';
import { useTranslation } from 'react-i18next';
import InputField from '../../form-elements/InputField';
import PricePerHourDropdown from '../../../dropdowns/PricePerHourDropdown';
import Button from '../../../buttons/Button';
import Divider from '../../../ui/Divider';
import {
  PricePerHour,
  MenuItem,
  FieldPrice,
  Currency,
} from '../../../../types';
import { getObjectTranslation } from '../../../../utils/language';
import { toast } from 'react-toastify';
import ApplyPriceModal from './ApplyPriceModal';

type Props = {
  title: string;
  prices: FieldPrice[];
  onAddPrice: (
    startTime: string,
    endTime: string,
    price: PricePerHour,
    days: number[]
  ) => void;
  onRemovePrice: (startTime: string, endTime: string, day: number) => void;
  complexOpeningHours: string;
  complexClosingHours: string;
  day: number;
  currency: Currency;
  error?: string;
  defaultOpen?: boolean;
};

const formatTime = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (!digits) return '';

  const hour = digits.slice(0, 2).padStart(2, '0');
  const minute = digits.slice(2).padEnd(2, '0');

  return `${hour}:${minute}`;
};

export const PriceDisclosure = ({
  title,
  prices,
  day,
  onAddPrice,
  onRemovePrice,
  complexClosingHours,
  complexOpeningHours,
  currency,
  defaultOpen,
}: Props) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedPrice, setSelectedPrice] =
    useState<MenuItem<PricePerHour> | null>(null);

  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const [openModal, setOpenModal] = useState(false);

  const { t, i18n } = useTranslation();

  const isValid = useMemo(() => {
    if (!selectedPrice || !startTime || !endTime) {
      return false;
    }

    return true;
  }, [selectedPrice, startTime, endTime]);

  const currencyDisplay = useMemo(
    () => getObjectTranslation(currency, i18n.language),
    [i18n.language, currency]
  );

  const isTimeInRange = useCallback(
    (time: string) => {
      const [hours] = time.split(':').map(Number);
      const [openingHours] = complexOpeningHours.split(':').map(Number);
      const [closingHours] = complexClosingHours.split(':').map(Number);

      return hours >= openingHours && hours <= closingHours;
    },
    [complexClosingHours, complexOpeningHours]
  );

  const validateHours = useCallback(() => {
    let error: string | undefined;

    if (!startTime || !endTime) {
      error = t(COMMON_TRANSLATE_KEYS.PRICE_TIME_REQUIRED);
    } else if (!/^\d{2}:(0){2}$/.test(startTime)) {
      error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
        closing: complexClosingHours,
        opening: complexOpeningHours,
      });
    } else if (!isTimeInRange(startTime)) {
      error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
        closing: complexClosingHours,
        opening: complexOpeningHours,
      });
    } else if (!/^\d{2}:(0){2}$/.test(endTime)) {
      error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
        closing: complexClosingHours,
        opening: complexOpeningHours,
      });
    } else if (!isTimeInRange(endTime)) {
      error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
        closing: complexClosingHours,
        opening: complexOpeningHours,
      });
    } else if (startTime >= endTime) {
      error = t(COMMON_TRANSLATE_KEYS.START_END_HOURS_ERROR);
    }

    return error;
  }, [
    complexClosingHours,
    complexOpeningHours,
    endTime,
    isTimeInRange,
    startTime,
    t,
  ]);

  const validateStartTime = useCallback(
    (time: string) => {
      let error = '';

      if (!/^\d{2}:\d{2}$/.test(time) || !isTimeInRange(time)) {
        error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
          closing: complexClosingHours,
          opening: complexOpeningHours,
        });
      }

      return error;
    },
    [complexClosingHours, complexOpeningHours, isTimeInRange, t]
  );

  const validateEndTime = useCallback(
    (time: string) => {
      let error: string = '';

      if (!/^\d{2}:\d{2}$/.test(time) || !isTimeInRange(time)) {
        error = t(COMMON_TRANSLATE_KEYS.PRICE_HOURS_ERROR, {
          closing: complexClosingHours,
          opening: complexOpeningHours,
        });
      }

      return error;
    },
    [complexClosingHours, complexOpeningHours, isTimeInRange, t]
  );

  const handleStartTimeBlur = useCallback(() => {
    const formattedTime = formatTime(startTime);
    setStartTime(formattedTime);
    setStartTimeError(validateStartTime(formattedTime));
  }, [startTime, validateStartTime]);

  const handleEndTimeBlur = useCallback(() => {
    const formattedTime = formatTime(endTime);
    setEndTime(formattedTime);
    setEndTimeError(validateEndTime(formattedTime));
  }, [endTime, validateEndTime]);

  const handlePriceSelect = useCallback((option: MenuItem<PricePerHour>) => {
    setSelectedPrice(option);
  }, []);

  const handleAddPrice = useCallback(
    (days: number[]) => {
      if (!isValid) {
        return;
      }

      if (!selectedPrice) {
        return;
      }

      const hoursError = validateHours();

      if (hoursError) {
        toast.error(hoursError);
        return;
      }

      onAddPrice(startTime, endTime, selectedPrice.value, days);

      setStartTime('');
      setEndTime('');
      setSelectedPrice(null);
    },
    [endTime, isValid, onAddPrice, selectedPrice, startTime, validateHours]
  );

  return (
    <>
      <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => (
          <div className="bg-backGroundGray rounded-2xl">
            <Disclosure.Button className="flex w-full justify-between px-4 py-3 text-left text-mainWhite rounded-2xl items-center">
              <span>{title}</span>
              <ChevronUpIcon
                className={`${
                  !open ? 'rotate-180 transform' : ''
                } h-5 w-5 text-mainWhite`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="p-4 text-sm text-gray-500 bg-backGroundGray rounded-2xl flex flex-col gap-3 border-t-[1px] border-t-additionalGray rounded-t-none">
              <div className="w-full flex justify-between p-2 rounded-2xl items-start">
                <div className="grid grid-rows-1 grid-cols-3 w-full">
                  <div className="col-span-1">
                    <p className="text-mainWhite">
                      {t(COMMON_TRANSLATE_KEYS.FROM)}
                      <span className="text-functionalRed">*</span>
                    </p>

                    <InputField
                      className="border border-additionalGray"
                      placeholder={`17:00`}
                      width={10}
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      onBlur={() => handleStartTimeBlur()}
                      error={startTimeError}
                    />
                  </div>
                  <div className="col-span-1">
                    <p className="text-mainWhite">
                      {t(COMMON_TRANSLATE_KEYS.UNTIL)}
                      <span className="text-functionalRed">*</span>
                    </p>

                    <InputField
                      className="border border-additionalGray"
                      placeholder={`20:00`}
                      width={10}
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      onBlur={() => handleEndTimeBlur()}
                      error={endTimeError}
                    />
                  </div>
                  <div className="col-span-1">
                    <p className="text-mainWhite">
                      {t(COMMON_TRANSLATE_KEYS.PRICE)}
                      <span className="text-functionalRed">*</span>
                    </p>
                    <PricePerHourDropdown
                      selectedPrice={selectedPrice}
                      setSelectedPrice={handlePriceSelect}
                      showIcon={false}
                      width={10}
                      height={3}
                      className="border border-additionalGray w-[9rem]"
                      menuWidth={10}
                      placeholder={t(COMMON_TRANSLATE_KEYS.SELECT)}
                      menuOutline
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-end mt-6 h-full">
                  <Button
                    backgroundColor="bg-functionalGreen"
                    isDisabled={!isValid}
                    onClick={() => setOpenModal(true)}
                  >
                    <PlusIcon className="h-5 w-5 text-mainWhite" />
                  </Button>
                </div>
              </div>

              {prices.length > 0 && <Divider mb={0} mt={0} />}

              {prices.map((p) => (
                <div className="w-full flex justify-between p-2 rounded-2xl items-center">
                  <div className="grid grid-rows-2 grid-cols-3 w-full">
                    <p className="col-span-1">
                      {t(COMMON_TRANSLATE_KEYS.FROM)}
                    </p>
                    <p className="col-span-1">
                      {t(COMMON_TRANSLATE_KEYS.UNTIL)}
                    </p>
                    <p className="col-span-1">
                      {t(COMMON_TRANSLATE_KEYS.PRICE)}
                    </p>

                    <p className="col-span-1 text-mainWhite">{p.startTime}</p>
                    <p className="col-span-1 text-mainWhite">{p.endTime}</p>
                    <p className="col-span-1 text-mainWhite">{`${
                      p.price
                    } ${currencyDisplay}`}</p>
                  </div>
                  <div className="flex flex-col justify-end">
                    <Button
                      border="border border-functionalRed"
                      onClick={() => onRemovePrice(p.startTime, p.endTime, day)}
                    >
                      <XMarkIcon className="h-5 w-5 text-functionalRed" />
                    </Button>
                  </div>
                </div>
              ))}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      <ApplyPriceModal
        open={openModal}
        setOpen={setOpenModal}
        day={day}
        start={startTime}
        end={endTime}
        price={selectedPrice?.value.value}
        currency={currencyDisplay}
        onApply={(days) => handleAddPrice(days)}
      />
    </>
  );
};
